import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as assetsApi from '../api/assets';
import * as sessionsApi from '../api/sessions';
import * as incidentsApi from '../api/incidents';
import * as alertsApi from '../api/alerts';
import * as workOrdersApi from '../api/workOrders';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'uptime', label: 'Uptime' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'service', label: 'Service' }
];

export function AssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [asset, setAsset] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);

  const loadAsset = useCallback(async () => {
    if (!selectedOrg || !canReadAssets || !assetId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const assetData = await assetsApi.getAsset(selectedOrg.id, assetId);
      setAsset(assetData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, assetId]);

  const loadTabData = useCallback(async () => {
    if (!selectedOrg || !assetId) return;

    setTabLoading(true);
    try {
      switch (activeTab) {
        case 'sessions':
          const sessionsResult = await sessionsApi.getSessions(selectedOrg.id, { assetId, limit: 20 });
          setSessions(sessionsResult.sessions || []);
          break;
        case 'uptime':
          const incidentsResult = await incidentsApi.getIncidents(selectedOrg.id, { assetId, limit: 20 });
          setIncidents(incidentsResult.incidents || []);
          break;
        case 'alerts':
          const alertsResult = await alertsApi.getAlerts(selectedOrg.id, { assetId, limit: 20 });
          setAlerts(alertsResult.alerts || []);
          break;
        case 'service':
          const woResult = await workOrdersApi.getWorkOrders(selectedOrg.id, { assetId, limit: 20 });
          setWorkOrders(woResult.workOrders || []);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Failed to load tab data:', err);
    } finally {
      setTabLoading(false);
    }
  }, [selectedOrg, assetId, activeTab]);

  useEffect(() => {
    loadAsset();
  }, [loadAsset]);

  useEffect(() => {
    if (asset) {
      loadTabData();
    }
  }, [activeTab, asset, loadTabData]);

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view this asset.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading asset...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
        <button onClick={() => navigate('/fleet')} style={styles.backButton}>
          Back to Fleet
        </button>
      </div>
    );
  }

  if (!asset) {
    return (
      <div style={styles.container}>
        <div style={styles.notFound}>Asset not found</div>
        <button onClick={() => navigate('/fleet')} style={styles.backButton}>
          Back to Fleet
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title={asset.name}
        subtitle={asset.serialNumber}
        breadcrumbs={[
          { label: 'Fleet', href: '/fleet' },
          { label: asset.name }
        ]}
      />

      <div style={styles.tabs} data-testid="asset-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.activeTab : {})
            }}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.tabContent}>
        {activeTab === 'overview' && (
          <OverviewTab asset={asset} />
        )}
        {activeTab === 'sessions' && (
          <SessionsTab sessions={sessions} loading={tabLoading} />
        )}
        {activeTab === 'uptime' && (
          <UptimeTab incidents={incidents} loading={tabLoading} />
        )}
        {activeTab === 'alerts' && (
          <AlertsTab alerts={alerts} loading={tabLoading} />
        )}
        {activeTab === 'compliance' && (
          <PlaceholderTab name="Compliance" />
        )}
        {activeTab === 'service' && (
          <ServiceTab workOrders={workOrders} loading={tabLoading} assetId={assetId} />
        )}
      </div>
    </div>
  );
}

function OverviewTab({ asset }) {
  return (
    <div style={styles.overviewGrid} data-testid="overview-tab">
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Asset Information</h3>
        <div style={styles.infoRow}>
          <span style={styles.label}>Name:</span>
          <span>{asset.name}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Serial Number:</span>
          <span>{asset.serialNumber}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Model:</span>
          <span>{asset.model}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Status:</span>
          <span style={{
            ...styles.statusBadge,
            background: getStatusColor(asset.status).bg,
            color: getStatusColor(asset.status).text
          }}>
            {asset.status}
          </span>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Location</h3>
        <div style={styles.infoRow}>
          <span style={styles.label}>Site:</span>
          <span>{asset.siteName}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Coordinates:</span>
          <span>{asset.latitude}, {asset.longitude}</span>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Program & Ownership</h3>
        <div style={styles.infoRow}>
          <span style={styles.label}>Program:</span>
          <span>{asset.program}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Owner:</span>
          <span>{asset.owner}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.label}>Install Date:</span>
          <span>{asset.installDate}</span>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Ports</h3>
        {asset.ports?.map(port => (
          <div key={port.id} style={styles.portRow}>
            <span>Port {port.number}</span>
            <span>{port.type}</span>
            <span>{port.maxPower} kW</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionsTab({ sessions, loading }) {
  if (loading) return <div style={styles.tabLoading}>Loading sessions...</div>;

  return (
    <div data-testid="sessions-tab">
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Start Time</th>
            <th style={styles.th}>Duration</th>
            <th style={styles.th}>Energy (kWh)</th>
            <th style={styles.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map(session => (
            <tr key={session.id} style={styles.tr}>
              <td style={styles.td}>{new Date(session.startTime).toLocaleString()}</td>
              <td style={styles.td}>{session.duration ? `${session.duration} min` : '-'}</td>
              <td style={styles.td}>{session.energyDelivered?.toFixed(1) || '-'}</td>
              <td style={styles.td}>{session.status}</td>
            </tr>
          ))}
          {sessions.length === 0 && (
            <tr>
              <td colSpan="4" style={styles.emptyState}>No sessions found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UptimeTab({ incidents, loading }) {
  if (loading) return <div style={styles.tabLoading}>Loading uptime data...</div>;

  return (
    <div data-testid="uptime-tab">
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Start Time</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Duration</th>
            <th style={styles.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {incidents.map(incident => (
            <tr key={incident.id} style={styles.tr}>
              <td style={styles.td}>{new Date(incident.startTime).toLocaleString()}</td>
              <td style={styles.td}>{incident.type}</td>
              <td style={styles.td}>{incident.durationMinutes ? `${incident.durationMinutes} min` : 'Ongoing'}</td>
              <td style={styles.td}>{incident.status}</td>
            </tr>
          ))}
          {incidents.length === 0 && (
            <tr>
              <td colSpan="4" style={styles.emptyState}>No incidents found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AlertsTab({ alerts, loading }) {
  if (loading) return <div style={styles.tabLoading}>Loading alerts...</div>;

  return (
    <div data-testid="alerts-tab">
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Time</th>
            <th style={styles.th}>Severity</th>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Ack Notes</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map(alert => (
            <tr key={alert.id} style={styles.tr} data-testid={`alert-row-${alert.id}`}>
              <td style={styles.td}>{new Date(alert.createdAt).toLocaleString()}</td>
              <td style={styles.td}>
                <span style={{
                  ...styles.severityBadge,
                  background: getSeverityColor(alert.severity)
                }}>
                  {alert.severity}
                </span>
              </td>
              <td style={styles.td}>
                <div style={styles.alertTitle}>{alert.title}</div>
                <div style={styles.alertMessage}>{alert.message}</div>
              </td>
              <td style={styles.td}>
                {alert.acknowledged ? (
                  <span style={styles.ackBadge}>Acknowledged</span>
                ) : (
                  <span style={styles.unackBadge}>Unacknowledged</span>
                )}
              </td>
              <td style={styles.td} data-testid={`ack-notes-${alert.id}`}>
                {alert.acknowledgedNotes || '-'}
              </td>
            </tr>
          ))}
          {alerts.length === 0 && (
            <tr>
              <td colSpan="5" style={styles.emptyState}>No alerts found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ServiceTab({ workOrders, loading, assetId }) {
  if (loading) return <div style={styles.tabLoading}>Loading maintenance history...</div>;

  const completedOrders = workOrders.filter(wo => wo.status === 'completed');
  const totalCost = completedOrders.reduce((sum, wo) => sum + (wo.totalCost || 0), 0);
  const totalHours = completedOrders.reduce((sum, wo) => sum + (wo.laborHours || 0), 0);

  return (
    <div data-testid="service-tab">
      <div style={styles.serviceSummary}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryValue}>{workOrders.length}</span>
          <span style={styles.summaryLabel}>Total Work Orders</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryValue}>{completedOrders.length}</span>
          <span style={styles.summaryLabel}>Completed</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryValue}>{totalHours.toFixed(1)} hrs</span>
          <span style={styles.summaryLabel}>Total Labor</span>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryValue}>${totalCost.toFixed(2)}</span>
          <span style={styles.summaryLabel}>Total Cost</span>
        </div>
      </div>

      <h4 style={styles.sectionTitle}>Maintenance History</h4>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Assigned To</th>
            <th style={styles.th}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {workOrders.map(wo => (
            <tr key={wo.id} style={styles.tr} data-testid={`service-wo-${wo.id}`}>
              <td style={styles.td}>{new Date(wo.updatedAt).toLocaleDateString()}</td>
              <td style={styles.td}>
                <span style={styles.typeBadge}>{wo.type}</span>
              </td>
              <td style={styles.td}>
                <div style={styles.woTitle}>{wo.title}</div>
                {wo.resolution && (
                  <div style={styles.woResolution}>{wo.resolution}</div>
                )}
              </td>
              <td style={styles.td}>
                <span style={{
                  ...styles.statusBadge,
                  background: getWoStatusColor(wo.status).bg,
                  color: getWoStatusColor(wo.status).text
                }}>
                  {wo.status.replace('_', ' ')}
                </span>
              </td>
              <td style={styles.td}>{wo.assignedTo || '-'}</td>
              <td style={styles.td}>{wo.totalCost ? `$${wo.totalCost.toFixed(2)}` : '-'}</td>
            </tr>
          ))}
          {workOrders.length === 0 && (
            <tr>
              <td colSpan="6" style={styles.emptyState}>No maintenance history for this asset</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function getWoStatusColor(status) {
  const colors = {
    completed: { bg: '#c6f6d5', text: '#276749' },
    in_progress: { bg: '#bee3f8', text: '#2b6cb0' },
    open: { bg: '#fefcbf', text: '#975a16' },
    cancelled: { bg: '#fed7d7', text: '#c53030' }
  };
  return colors[status] || { bg: '#e2e8f0', text: '#4a5568' };
}

function PlaceholderTab({ name }) {
  return (
    <div style={styles.placeholder} data-testid={`${name.toLowerCase()}-tab`}>
      <h3>{name}</h3>
      <p>This tab will be implemented in a future story.</p>
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    online: { bg: '#c6f6d5', text: '#276749' },
    offline: { bg: '#fed7d7', text: '#c53030' },
    maintenance: { bg: '#fefcbf', text: '#975a16' }
  };
  return colors[status] || { bg: '#e2e8f0', text: '#4a5568' };
}

function getSeverityColor(severity) {
  const colors = {
    critical: '#fed7d7',
    warning: '#fefcbf',
    info: '#bee3f8'
  };
  return colors[severity] || '#e2e8f0';
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  notFound: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  backButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    cursor: 'pointer'
  },
  tabs: {
    display: 'flex',
    gap: '0.25rem',
    borderBottom: '1px solid #e2e8f0',
    marginBottom: '1.5rem'
  },
  tab: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#718096',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px'
  },
  activeTab: {
    color: '#2d3748',
    borderBottomColor: '#3182ce',
    fontWeight: '500'
  },
  tabContent: {
    minHeight: '400px'
  },
  tabLoading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f7fafc'
  },
  label: {
    color: '#718096',
    fontSize: '0.875rem'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  severityBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  portRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f7fafc',
    fontSize: '0.875rem'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  alertTitle: {
    fontWeight: '500',
    marginBottom: '0.25rem'
  },
  alertMessage: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  ackBadge: {
    color: '#276749',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  unackBadge: {
    color: '#c53030',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  placeholder: {
    padding: '3rem',
    textAlign: 'center',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    color: '#718096'
  },
  serviceSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  summaryCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  summaryValue: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  summaryLabel: {
    display: 'block',
    fontSize: '0.75rem',
    color: '#718096',
    marginTop: '0.25rem'
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  typeBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    background: '#e2e8f0',
    color: '#4a5568',
    textTransform: 'capitalize'
  },
  woTitle: {
    fontWeight: '500',
    marginBottom: '0.25rem'
  },
  woResolution: {
    fontSize: '0.75rem',
    color: '#718096'
  }
};
